import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ExternalLink, FileText, Lock, Search } from '../../lib/icons';
import { fmt, StatBlock } from '../shared/badges';
import { PrintDoc } from '../print/PrintDoc';
import { urlPdfOfficiel } from '../../lib/emission';
import { ETATS_FACTURATION, fetchOperationsFacturables, filtrerOperations } from '../../lib/facturable';

const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';
const field =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';

const COULEUR_FACTURATION = {
  Facturée: 'bg-emerald-50 text-emerald-800',
  'À facturer': 'bg-sky-50 text-sky-800',
  'À corriger': 'bg-red-50 text-red-800',
  Annulée: 'bg-slate-200 text-slate-700',
};
const COULEUR_REGLEMENT = {
  Payé: 'bg-emerald-50 text-emerald-800',
  Créance: 'bg-amber-50 text-amber-800',
  'En attente': 'bg-slate-100 text-slate-600',
};

// Tableau de bord unique : toutes les opérations facturables (ventes, prestations,
// transports, divers) sans registre parallèle ni second chiffre d'affaires.
export const TableauFacturable = () => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ q: '', du: '', au: '', client: 'Tous', module: 'Tous', etat: 'Tous' });
  const [apercu, setApercu] = useState(null); // { row, emettre: bool }

  const load = useCallback(() => {
    fetchOperationsFacturables().then(setRows);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const clients = useMemo(() => ['Tous', ...[...new Set(rows.map((r) => r.tiers))].sort()], [rows]);
  const modules = useMemo(() => ['Tous', ...[...new Set(rows.map((r) => r.source))].sort()], [rows]);
  const visibles = useMemo(() => filtrerOperations(rows, f), [rows, f]);

  const somme = (liste) => liste.reduce((s, r) => s + (Number(r.montant) || 0), 0);
  const aFacturer = rows.filter((r) => r.etatFacturation === 'À facturer');
  const aCorriger = rows.filter((r) => r.etatFacturation === 'À corriger');
  const obligatoiresRestantes = rows.filter((r) => r.obligatoire && ['À facturer', 'À corriger'].includes(r.etatFacturation));

  const maj = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div data-testid="tableau-facturable" className="space-y-5">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <StatBlock
          testid="stat-a-facturer"
          titre="À facturer"
          accent="border-l-sky-600"
          items={[['Opérations', fmt(aFacturer.length)], ['Montant (TND)', fmt(somme(aFacturer), 3)]]}
        />
        <StatBlock
          testid="stat-a-corriger"
          titre="À corriger avant émission"
          accent="border-l-red-500"
          items={[['Opérations', fmt(aCorriger.length)], ['Montant (TND)', fmt(somme(aCorriger), 3)]]}
        />
        <StatBlock
          testid="stat-facturees"
          titre="Facturées officiellement"
          accent="border-l-emerald-600"
          items={[
            ['Opérations', fmt(rows.filter((r) => r.etatFacturation === 'Facturée').length)],
            ['Montant (TND)', fmt(somme(rows.filter((r) => r.etatFacturation === 'Facturée')), 3)],
          ]}
        />
        <StatBlock
          testid="stat-obligatoires"
          titre="Obligations d'émission restantes"
          accent="border-l-amber-500"
          items={[['Opérations', fmt(obligatoiresRestantes.length)], ['Montant (TND)', fmt(somme(obligatoiresRestantes), 3)]]}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 border border-slate-200 bg-white px-4 py-3 sm:grid-cols-2 lg:grid-cols-6">
        <div className="lg:col-span-2">
          <label className={label}>Recherche</label>
          <div className="mt-1.5 flex h-11 items-center gap-2 border border-slate-300 bg-slate-50 px-3 focus-within:border-slate-900 focus-within:bg-white">
            <Search size={14} className="text-slate-400" />
            <input
              data-testid="facturable-recherche"
              value={f.q}
              onChange={(e) => maj('q', e.target.value)}
              placeholder="Client, référence, n° de facture…"
              className="h-full w-full bg-transparent font-data text-sm outline-none"
            />
          </div>
        </div>
        <div>
          <label className={label}>Du</label>
          <input data-testid="facturable-du" type="date" value={f.du} onChange={(e) => maj('du', e.target.value)} className={field} />
        </div>
        <div>
          <label className={label}>Au</label>
          <input data-testid="facturable-au" type="date" value={f.au} onChange={(e) => maj('au', e.target.value)} className={field} />
        </div>
        <div>
          <label className={label}>Client</label>
          <select data-testid="facturable-client" value={f.client} onChange={(e) => maj('client', e.target.value)} className={field}>
            {clients.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={label}>Module source</label>
          <select data-testid="facturable-module" value={f.module} onChange={(e) => maj('module', e.target.value)} className={field}>
            {modules.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap gap-px bg-slate-200">
        {ETATS_FACTURATION.map((e) => (
          <button
            key={e}
            type="button"
            data-testid={`facturable-etat-${e.toLowerCase().replace(/\s|à/g, '-')}`}
            onClick={() => maj('etat', e)}
            className={`px-3 py-2.5 text-sm font-bold transition-colors duration-150 ${
              f.etat === e ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            {e}
          </button>
        ))}
      </div>

      <section className="border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-facturable">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Date', 'Réf. origine', 'Module', 'Client', 'Opération', 'Montant (TND)', 'Facturation', 'N° facture', 'Règlement', 'À compléter', 'Actions'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibles.length === 0 ? (
                <tr data-testid="facturable-empty">
                  <td colSpan={11} className="px-3 py-12 text-center text-sm text-slate-500">
                    Aucune opération facturable ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                visibles.map((r) => (
                  <tr key={r.cle} data-testid={`facturable-row-${r.ref}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-data text-sm text-slate-700">{r.date}</td>
                    <td className="px-3 py-2 font-data text-xs text-slate-500">{r.ref}</td>
                    <td className="px-3 py-2 font-data text-xs text-slate-500">{r.source}</td>
                    <td className="px-3 py-2 text-sm font-semibold text-slate-900">{r.tiers}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{r.categorie}</td>
                    <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(r.montant, 3)}</td>
                    <td className="px-3 py-2">
                      <span
                        data-testid={`etat-facturation-${r.ref}`}
                        className={`font-data text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 ${COULEUR_FACTURATION[r.etatFacturation]}`}
                      >
                        {r.etatFacturation}
                      </span>
                      {r.obligatoire && r.etatFacturation !== 'Facturée' && (
                        <span className="ml-1.5 font-data text-[10px] uppercase tracking-[0.08em] text-amber-700"> · obligatoire</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">
                      {r.numeroOfficiel ? (
                        <a
                          data-testid={`facture-lien-${r.ref}`}
                          href={urlPdfOfficiel(r.numeroOfficiel)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-emerald-800 underline decoration-emerald-300"
                        >
                          <Lock size={12} /> {r.numeroOfficiel} <ExternalLink size={11} />
                        </a>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        data-testid={`reglement-${r.ref}`}
                        className={`font-data text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 ${COULEUR_REGLEMENT[r.reglement] ?? 'bg-slate-100 text-slate-600'}`}
                      >
                        {r.reglement}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[11px]">
                      {r.blocages.length > 0 ? (
                        <span data-testid={`blocages-${r.ref}`} className="inline-flex items-start gap-1 text-red-700">
                          <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {r.blocages.join(' · ')}
                        </span>
                      ) : r.avertissements.length > 0 ? (
                        <span data-testid={`avertissements-${r.ref}`} className="text-amber-700">{r.avertissements.join(' · ')}</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap justify-end gap-1">
                        <button
                          type="button"
                          data-testid={`consulter-${r.ref}`}
                          onClick={() => setApercu({ row: r, emettre: false })}
                          className="border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-900 hover:text-white"
                        >
                          Consulter
                        </button>
                        {r.etatFacturation === 'À facturer' && (
                          <button
                            type="button"
                            data-testid={`preparer-${r.ref}`}
                            onClick={() => setApercu({ row: r, emettre: true })}
                            className="inline-flex items-center gap-1 border-2 border-slate-900 px-2 py-1 text-xs font-bold text-slate-900 hover:bg-slate-900 hover:text-white"
                          >
                            <FileText size={12} /> Prévisualiser & émettre
                          </button>
                        )}
                        {r.numeroOfficiel && (
                          <a
                            data-testid={`reimprimer-${r.ref}`}
                            href={urlPdfOfficiel(r.numeroOfficiel)}
                            target="_blank"
                            rel="noreferrer"
                            className="border border-emerald-300 px-2 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-50"
                          >
                            Réimprimer
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-xs text-slate-500">{visibles.length} opération(s) affichée(s) — recettes suivies en comptabilité quel que soit l'état de facturation</p>
          <p data-testid="facturable-total" className="font-data text-sm font-semibold text-slate-900">
            Total affiché : {fmt(somme(visibles), 3)} TND
          </p>
        </div>
      </section>

      {apercu && (
        <PrintDoc
          type={apercu.emettre ? 'Facture — aperçu avant émission' : `Consultation — ${apercu.row.categorie}`}
          numero={apercu.row.numeroOfficiel ?? apercu.row.ref}
          date={apercu.row.date}
          tiers={apercu.row.tiers}
          lignes={[
            ['Module d’origine', apercu.row.source],
            ['Référence interne', apercu.row.ref],
            ['Matricule fiscal client', apercu.row.mfClient || '—'],
            ...(apercu.row.lignes ?? []),
          ]}
          totaux={[['Montant total', `${fmt(apercu.row.montant, 3)} TND`]]}
          mentions={`État de facturation : ${apercu.row.etatFacturation}. Règlement : ${apercu.row.reglement}.${
            apercu.emettre ? '' : ' Consultation sans effet sur les données.'
          }`}
          emission={
            apercu.emettre
              ? {
                  type: apercu.row.operationType,
                  id: apercu.row.ref,
                  libelle: apercu.row.categorie,
                  date: apercu.row.date,
                  tiersId: apercu.row.tiersId,
                  tiers: apercu.row.tiers,
                  tiersMatricule: apercu.row.mfClient || null,
                  referenceInterne: apercu.row.ref,
                  total: apercu.row.montant,
                  lignes: apercu.row.lignes,
                  totaux: [['Montant total', `${fmt(apercu.row.montant, 3)} TND`]],
                  onEmis: load,
                }
              : undefined
          }
          onClose={() => {
            setApercu(null);
            load();
          }}
        />
      )}
    </div>
  );
};
